import { useCallback, useEffect, useState } from 'react';
import { getFontScale, isMobileDevice, isSafari } from '../../util';

/**
 * Breakpoints supported in our application.
 */
export enum Breakpoints {
  /** Mobile devices, small browsers */
  MOBILE = 768,
  /** Small browsers. */
  SMALL = 1024,
  /** medium sized browsers */
  MEDIUM = 1200,
  /** Large browsers, but not quite 1080p */
  LARGE = 1600
}

/**
 * Copied from react-laag
 *
 * MIT License
 * Copyright (c) 2019 Erik Verweij
 *
 */
function useBreakpointInternal(maxPixels: number): boolean {
  const [match, setMatch] = useState(
    typeof window !== 'undefined'
      ? window.matchMedia('(max-width: ' + maxPixels + 'px)').matches
      : false
  );
  useEffect(() => {
    const matcher = window.matchMedia('(max-width: ' + maxPixels + 'px)');

    function onMatch(evt: MediaQueryListEvent) {
      setMatch(evt.matches);
    }

    matcher.addEventListener('change', onMatch);
    return () => {
      matcher.removeEventListener('change', onMatch);
    };
  }, [maxPixels]);
  return match;
}

/**
 *
 */
export function useBreakpoint(bp: Breakpoints): boolean {
  return useBreakpointInternal(bp);
}

/**
 * Hook to determine if the device size is below a specific height in pixels.
 *
 * This is only meant for special cases where you need to react on a device's height.
 * In most normal circumstances, you should use {@link useBreakpoint}!
 */
export function useHeightBreakpoint(maxPixels: number): boolean {
  const [match, setMatch] = useState(
    typeof window !== 'undefined'
      ? window.matchMedia('(max-height: ' + maxPixels + 'px)').matches
      : false
  );
  useEffect(() => {
    const matcher = window.matchMedia('(max-height: ' + maxPixels + 'px)');

    function onMatch(evt: MediaQueryListEvent) {
      setMatch(evt.matches);
    }

    matcher.addEventListener('change', onMatch);
    return () => {
      matcher.removeEventListener('change', onMatch);
    };
  }, [maxPixels]);
  return match;
}

/**
 * Hook to determine whether the window zoom and/or font zoom is above a specific breakpoint.
 *
 * The regular zoom and font zoom are combined multiplicatively, meaning the breakpoint will trigger if they are above the breakpoint
 * when combined, even if they're not above it individually.
 * In most cases either one of the two will be 1. But there are edge-cases where both could be above or below 1, like when your
 * display scaling is different, and you have a font scaling active at the same time.
 *
 * @param breakpoint  breakpoint in percentage (e.g. 1.5 is 150%)
 */
export function useZoomBreakpoint(breakpoint: number): boolean {
  const compute = useCallback(() => {
    let devicePixelRatio = window.devicePixelRatio;
    if (isSafari() || isMobileDevice()) {
      // Safari (WebKit) doesn't fully support `window.devicePixelRatio` — it
      // always returns a fixed value like 2 or 3 regardless of actual zoom.
      // https://developer.mozilla.org/en-US/docs/Web/API/Window/devicePixelRatio#browser_compatibility
      // The same problem applies more broadly to all mobile devices (iOS and
      // Android, regardless of browser engine): devicePixelRatio reflects the
      // screen's native pixel density, not the user's zoom level.
      // Therefore, we normalize it to 1 on mobile and rely on font scale instead.
      devicePixelRatio = 1;
    }
    return devicePixelRatio * getFontScale();
  }, []);

  const [zoom, setZoom] = useState(compute());

  useEffect(() => {
    const update = () => setZoom(compute());

    // ResizeObserver to observe most changes, including computed font-size changes
    const rootEl = document.getElementById('content');
    let resizeObserver: ResizeObserver | null = null;

    if (rootEl) {
      resizeObserver = new ResizeObserver(update);
      resizeObserver.observe(rootEl);
    }

    // devicePixelRatio changes on zoom or display changes
    const mq = window.matchMedia(
      `(resolution: ${window.devicePixelRatio}dppx)`
    );
    mq.addEventListener('change', update);

    // fallback: resize event also catches some zoom changes
    window.addEventListener('resize', update);

    return () => {
      resizeObserver?.disconnect();
      mq.removeEventListener('change', update);
      window.removeEventListener('resize', update);
    };
  }, [compute]);

  return zoom >= breakpoint;
}
